import { useTranslation } from 'react-i18next';
import { Link, useOutletContext } from 'react-router-dom';

import { Button } from '~/components/Button';
import { Icon } from '~/components/Icon';
import { cn } from '~/lib/cn';
import { ArtistHeader } from '~/modules/artist-roster/pages/artist-details/components/ArtistHeader/ArtistHeader';
import type { ArtistDetails } from '~/modules/artist-roster/types';

export function DigitalCampaignsPage() {
  const { artistData } = useOutletContext<{ artistData: ArtistDetails }>();
  const { t } = useTranslation('campaign-planner');
  const benchmarksLink = `/roster/${artistData.partyId}/campaigns/benchmarks`;
  const plannerLink = `/roster/${artistData.partyId}/campaigns/planner`;
  const baseTileClassName = cn(
    'flex h-[446px] shrink-0 basis-[356px] flex-col rounded-md border px-xl py-3xl border-emphasis-subtle',
  );

  return (
    <>
      <ArtistHeader artistData={artistData} />
      <div className="flex w-[1392px] items-end justify-between gap-xl px-4xl pb-xl pt-3xl">
        <div className="flex h-[446px] flex-col justify-center">
          <h1 className="mb-md text-body-lg font-bold">
            {t('digitalCampaigns.digitalCampaigns')}
          </h1>
          <p className="text-body-md leading-none">
            {t('digitalCampaigns.useExisting')}
          </p>
        </div>

        {/* ---------------------------------------
            Show benchmarks tile.
        --------------------------------------- */}
        <Link
          to={benchmarksLink}
          aria-label={t('digitalCampaigns.showBenchmarks')}
          className={cn(
            baseTileClassName,
            'bg-emphasis-default hover:bg-emphasis-strong hover:border-emphasis-strong',
          )}
        >
          <div className="mb-md grid size-[72px] place-items-center rounded-full bg-emphasis-default">
            <Icon type="chart-simple-horizontal" variant="light" size="2xl" />
          </div>
          <h1 className="mb-sm text-headline-md font-semibold leading-none">
            {t('digitalCampaigns.showDigital')}
          </h1>
          <p className="flex-grow text-body-sm leading-tight">
            {t('digitalCampaigns.usePrevious')}
          </p>

          <Button
            variant="secondary"
            size="xlarge"
            className="size-fit hover:bg-transparent hover:outline-default"
            asChild
          >
            <div>{t('digitalCampaigns.showBenchmarks')}</div>
          </Button>
        </Link>

        {/* ---------------------------------------
            Create plan tile.
        --------------------------------------- */}
        <Link
          to={plannerLink}
          aria-label={t('digitalCampaigns.createPlan')}
          className={cn(
            baseTileClassName,
            'bg-[linear-gradient(180deg,oklch(var(--bg-primary-default))35%,oklch(var(--bg-secondary-disabled)))]',
            // NOTE: We have to use another linear gradient here because just using a background will not cover the
            //       existing gradient.
            'hover:bg-[linear-gradient(0deg,oklch(var(--bg-secondary-disabled)),oklch(var(--bg-secondary-disabled)))]',
          )}
        >
          <div className="mb-md grid size-[72px] place-items-center rounded-full bg-emphasis-default">
            <Icon type="list-check" variant="solid" size="2xl" />
          </div>
          <h1 className="mb-sm text-headline-md font-semibold leading-none">
            {t('digitalCampaigns.createDigital')}
          </h1>
          <p className="flex-grow text-body-sm leading-tight">
            {t('digitalCampaigns.defineObjectives')}
          </p>
          <Button
            variant="secondary"
            size="xlarge"
            className="size-fit hover:bg-transparent hover:outline-default"
            asChild
          >
            <div>{t('digitalCampaigns.createPlan')}</div>
          </Button>
        </Link>
      </div>
    </>
  );
}

DigitalCampaignsPage.displayName = 'DigitalCampaignsPage';
